"use client";
import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import EmergencyHelpCard from "@/app/(landing)/mental-health/components/emergency-help-card";
import { motion } from "framer-motion";

/**
 * Props for `EmergencyImmediateHelp`.
 */
export type EmergencyImmediateHelpProps =
  SliceComponentProps<Content.EmergencyImmediateHelpSlice>;

/**
 * Component for "EmergencyImmediateHelp" Slices.
 */
const EmergencyImmediateHelp: FC<EmergencyImmediateHelpProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === "default" ? (
        <div className="bg-[#FAFDFF]">
          <Bounded className="py-20 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="space-y-4"
            >
              {/* heading */}

              <PrismicRichText
                field={slice.primary.heading}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h1>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h2>
                  ),
                  heading3: ({ children }) => (
                    <h3 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h3>
                  ),
                  heading4: ({ children }) => (
                    <h4 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h4>
                  ),
                  heading5: ({ children }) => (
                    <h5 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h5>
                  ),
                  heading6: ({ children }) => (
                    <h6 className="text-3xl md:text-4xl lg:text-5xl md:text-center font-semibold text-primary">
                      {children}
                    </h6>
                  ),
                  label: ({ node, children }) => {
                    if (node.data.label === "Highlight") {
                      return <span className="text-secondary">{children}</span>;
                    }
                  },
                }}
              />

              <PrismicRichText
                field={slice.primary.sub_heading}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg md:text-xl md:text-center font-medium font-merriweather text-primary">
                      {children}
                    </p>
                  ),
                }}
              />
            </motion.div>
            {/* cards */}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {slice.primary.emergency_help_cards.map((item, index) => (
                <EmergencyHelpCard key={index} data={item} index={index} />
              ))}
            </div>

            {/* overlays */}
          </Bounded>
        </div>
      ) : null}
    </section>
  );
};

export default EmergencyImmediateHelp;
